/**
 * Web-side client for the Postgres-backed teams endpoints: the listing
 * (`GET /api/v2/teams`) and one team's members (`GET /api/v2/teams/{team_id}/members`),
 * which are public, and the summary (`GET /api/v2/internal/teams/summary`), which
 * is internal: the web app's own, undocumented, and answered only for this
 * browser's registered web client device (see `internalApi.ts`).
 *
 * Modeled on `membersApi.ts`: a pure, Jest-testable kernel that reuses the
 * caller's existing sign-in. The Vue layer supplies `idToken` (`getIdToken()`)
 * and `baseUrl`. Postgres decides which rows exist and in what order, already
 * scoped to what the caller may read; every id it returns is one the browser
 * can then bind to Firestore for liveness. The summary counts exactly the teams
 * `GET /teams` would list with the same filters, split into activated and
 * deactivated; `scope` says whether that is every team in the company or only
 * the caller's own.
 */

import { ListFetchFailure, ListFetchResult, ListPage } from '@/utils/cursorWalk';
import { InternalApiCredentials, internalApiHeaders, internalApiPath } from '@/utils/internalApi';

export type TeamsSummaryScope = 'all' | 'joined';
export type TeamsSummaryMemberStatus = 'joined' | 'pending';

export interface TeamsSummaryRequest {
	companyId: string;
	// The account to act as: the caller's currently selected team, sent as
	// `team_id` the way every other api2 call names its account. It is not a
	// filter, and the summary never narrows to it.
	accountTeamId?: string | null;
	q?: string | null;
	// One person's teams, by their exact address.
	memberEmail?: string | null;
	memberStatus?: TeamsSummaryMemberStatus | null;
}

export interface TeamsSummaryCounts {
	teamCount: number;
	// People who have joined at least one of those teams, each counted once.
	memberCount: number;
}

export interface TeamsSummary {
	scope: TeamsSummaryScope;
	active: TeamsSummaryCounts;
	deactivated: TeamsSummaryCounts;
}

export interface TeamsSummaryFailure {
	ok: false;
	reason: 'network' | 'unauthorized' | 'forbidden' | 'failed';
	// The server's structured error envelope ({ error: { code, message } }).
	code?: string;
	message?: string;
}

export type TeamsSummaryResult =
	| { ok: true; summary: TeamsSummary }
	| TeamsSummaryFailure;

/**
 * The failure, or null when the fetch worked. See `membersFetchFailure` for
 * why this is a function: `web` compiles with `strictNullChecks: false`, where
 * `if (!result.ok)` does not narrow the union.
 */
export function teamsSummaryFailure(result: TeamsSummaryResult): TeamsSummaryFailure | null {
	return result.ok === true ? null : result;
}

export interface TeamsSummaryFetchInput extends InternalApiCredentials {
	baseUrl: string;
	request: TeamsSummaryRequest;
	// Injectable so specs exercise the real request path without patching
	// the global; the page leaves it unset.
	fetchImpl?: typeof fetch;
}

/** Same origin the Library, members and boards clients default to. */
export const TEAMS_API_BASE_URL: string =
	process.env.VUE_APP_API_BASE_URL || 'https://api2.rundiffusion.com/api/v2';

// The endpoint's cap on `q`. A longer value is a 400 for the whole request.
export const TEAMS_Q_MAX_LENGTH = 200;

function present(value?: string | null): string | null {
	return value && value.trim() ? value.trim() : null;
}

/** The page's camelCase request as the endpoint's snake_case query string. */
export function buildTeamsSummaryQueryParams(request: TeamsSummaryRequest): URLSearchParams {
	const params = new URLSearchParams();
	// Which company to count, and which account we are counting it as: two
	// questions, two parameters (see buildMembersQueryParams).
	params.set('company_id', request.companyId);
	const accountTeamId = present(request.accountTeamId);
	if (accountTeamId) params.set('team_id', accountTeamId);

	const q = present(request.q);
	if (q) params.set('q', q.slice(0, TEAMS_Q_MAX_LENGTH));
	const memberEmail = present(request.memberEmail);
	if (memberEmail) params.set('member_email', memberEmail.toLowerCase());
	if (memberEmail && request.memberStatus) params.set('member_status', request.memberStatus);
	return params;
}

/** The request carries the bearer token and nothing else. */
export function authedHeaders(idToken: string): Record<string, string> {
	return {
		'Authorization': `Bearer ${idToken}`,
	};
}

function objectRecord(value: unknown): Record<string, unknown> | null {
	return value !== null && typeof value === 'object' && !Array.isArray(value)
		? value as Record<string, unknown>
		: null;
}

function count(value: unknown): number {
	const n = Number(value);
	return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
}

function parseCounts(candidate: unknown): TeamsSummaryCounts {
	const counts = objectRecord(candidate) || {};
	return {
		teamCount: count(counts.team_count),
		memberCount: count(counts.member_count),
	};
}

/**
 * Normalize the wire body into the page's camelCase model. A missing or
 * unknown scope reads as the narrower one: a label that undersells is safer
 * than one that claims every team in the company.
 */
export function parseTeamsSummary(body: unknown): TeamsSummary {
	const envelope = objectRecord(body) || {};
	return {
		scope: envelope.scope === 'all' ? 'all' : 'joined',
		active: parseCounts(envelope.active),
		deactivated: parseCounts(envelope.deactivated),
	};
}

/** Best-effort read of `{ error: { code, message } }`. Never throws. */
async function readErrorEnvelope(resp: Response): Promise<{ code?: string; message?: string }> {
	try {
		const body: unknown = await resp.json();
		const err: Record<string, unknown> = objectRecord(objectRecord(body)?.error) || {};
		return {
			code: typeof err.code === 'string' ? err.code : undefined,
			message: typeof err.message === 'string' ? err.message : undefined,
		};
	} catch {
		return {};
	}
}

/**
 * The summary as a result union, so the page can branch on a failure without
 * catching. Internal: it needs this browser's web client device as well as the
 * token (`services/internalApiCredentials.ts`), and the page should hand a
 * refusal's code to `forgetWebClientDeviceIfRejected`.
 */
export async function fetchTeamsSummary(input: TeamsSummaryFetchInput): Promise<TeamsSummaryResult> {
	// No company, no request: the endpoint would answer 400.
	if (!present(input.request.companyId)) {
		return { ok: false, reason: 'failed', code: 'COMPANY_REQUIRED', message: 'A company is required' };
	}
	// No device, no request: the endpoint answers only a registered web browser.
	if (!present(input.deviceId)) {
		return { ok: false, reason: 'failed', code: 'WEB_APP_REGISTRATION_REQUIRED', message: 'This browser is not registered yet' };
	}
	const qs = buildTeamsSummaryQueryParams(input.request).toString();
	const url = `${input.baseUrl.replace(/\/$/, '')}${internalApiPath('/teams/summary')}?${qs}`;
	const fetchImpl: typeof fetch = input.fetchImpl || window.fetch;

	let resp: Response;
	try {
		resp = await fetchImpl(url, {
			method: 'GET',
			headers: internalApiHeaders(input),
		});
	} catch {
		return { ok: false, reason: 'network' };
	}

	if (!resp.ok) {
		const { code, message } = await readErrorEnvelope(resp);
		const reason: TeamsSummaryFailure['reason'] = resp.status === 401
			? 'unauthorized'
			: (resp.status === 403 ? 'forbidden' : 'failed');
		return {
			ok: false,
			reason,
			...(code ? { code } : {}),
			message: message || `HTTP ${resp.status}`,
		};
	}

	let body: unknown;
	try {
		body = await resp.json();
	} catch {
		return { ok: false, reason: 'failed', message: 'Invalid JSON' };
	}
	return { ok: true, summary: parseTeamsSummary(body) };
}

// ---------------------------------------------------------------------------
// The listings: the company's teams, and one team's members
// ---------------------------------------------------------------------------

export const TEAMS_MIN_LIMIT = 1;
export const TEAMS_MAX_LIMIT = 100;

export type TeamsSort = 'created' | 'name' | 'member_count' | 'custom_field';
export type TeamMembersSort = 'email' | 'joined' | 'invited';
export type ListOrder = 'asc' | 'desc';

export const TEAMS_DEFAULT_SORT: TeamsSort = 'created';
export const TEAM_MEMBERS_DEFAULT_SORT: TeamMembersSort = 'email';

/** The listing's own default direction: newest first, A to Z for the rest. */
export function defaultTeamsOrder(sort: TeamsSort | null | undefined): ListOrder {
	return (sort || TEAMS_DEFAULT_SORT) === 'created' ? 'desc' : 'asc';
}

/** Keep the page size inside the endpoints' accepted window. */
export function clampTeamsLimit(limit: number): number {
	const whole = Math.floor(Number(limit));
	if (!Number.isFinite(whole)) return TEAMS_MIN_LIMIT;
	return Math.min(TEAMS_MAX_LIMIT, Math.max(TEAMS_MIN_LIMIT, whole));
}

export interface TeamsListRequest {
	companyId: string;
	// The account to act as, sent as `team_id` the way every api2 call names it.
	accountTeamId?: string | null;
	cursor?: string | null;
	limit?: number;
	sort?: TeamsSort;
	order?: ListOrder | null;
	// Required by `sort: 'custom_field'`; without it that sort falls back to the default.
	customFieldKey?: string | null;
	// true for activated teams, false for deactivated, null for both.
	isActive?: boolean | null;
	q?: string | null;
	// One person's addresses, or several people's: the teams any of them has a
	// membership on. With exactly one, each row carries their membership.
	memberEmails?: string[] | null;
	memberStatus?: TeamsSummaryMemberStatus | null;
}

export interface TeamRoleRef {
	id: string;
	name: string | null;
}

export interface TeamCustomFieldValue {
	key: string;
	label: string;
	value: string | null;
}

/** With exactly one address in `memberEmails`: that person's membership on the team. */
export interface TeamMembershipRef {
	joined: string | null;
	invited: string | null;
	isPending: boolean;
	// The role held once joined, or the role the invitation will grant.
	teamRole: TeamRoleRef | null;
}

export interface TeamListRow {
	id: string;
	name: string;
	created: string | null;
	isActive: boolean;
	customFields: TeamCustomFieldValue[];
	memberCount: number;
	pendingCount: number;
	membership: TeamMembershipRef | null;
}

export interface TeamMembersRequest {
	// The team whose members these are: part of the path, never the account.
	teamId: string;
	companyId?: string | null;
	accountTeamId?: string | null;
	cursor?: string | null;
	limit?: number;
	sort?: TeamMembersSort;
	order?: ListOrder | null;
	q?: string | null;
	status?: TeamsSummaryMemberStatus | null;
	teamRoleId?: string | null;
}

export interface TeamMemberTeamRef {
	id: string;
	name: string;
	pending: boolean;
	isActive: boolean;
}

export interface TeamMemberListRow {
	// The normalized email: also the membership document's id.
	key: string;
	email: string;
	userId: string | null;
	invited: string | null;
	joined: string | null;
	isPending: boolean;
	companyRoleInviteOnly: boolean;
	teamRole: TeamRoleRef | null;
	companyRole: TeamRoleRef | null;
	teams: TeamMemberTeamRef[];
}

export function buildTeamsListQueryParams(request: TeamsListRequest): URLSearchParams {
	const params = new URLSearchParams();
	params.set('company_id', request.companyId);
	const accountTeamId = present(request.accountTeamId);
	if (accountTeamId) params.set('team_id', accountTeamId);
	if (request.isActive === true || request.isActive === false) {
		params.set('is_active', String(request.isActive));
	}

	const cursor = present(request.cursor);
	if (cursor) params.set('cursor', cursor);
	if (request.limit !== undefined && request.limit !== null) {
		params.set('limit', String(clampTeamsLimit(request.limit)));
	}

	const sort: TeamsSort = request.sort || TEAMS_DEFAULT_SORT;
	const customFieldKey = present(request.customFieldKey);
	// A custom field sort without its field is a 400; the default sort is the
	// closest thing the endpoint will run.
	const effectiveSort: TeamsSort = sort === 'custom_field' && !customFieldKey ? TEAMS_DEFAULT_SORT : sort;
	if (effectiveSort !== TEAMS_DEFAULT_SORT) params.set('sort', effectiveSort);
	if (effectiveSort === 'custom_field') params.set('custom_field_key', customFieldKey);
	if (request.order && request.order !== defaultTeamsOrder(effectiveSort)) {
		params.set('order', request.order);
	}

	const q = present(request.q);
	if (q) params.set('q', q.slice(0, TEAMS_Q_MAX_LENGTH));
	// Comma-separated, as boards' owners travel: each address trimmed,
	// lowercased and sent once.
	const memberEmails: string[] = Array.from(new Set(
		(request.memberEmails || [])
			.map((email: string) => present(email))
			.filter((email: string | null): email is string => !!email)
			.map((email: string) => email.toLowerCase()),
	));
	if (memberEmails.length) params.set('member_email', memberEmails.join(','));
	if (memberEmails.length && request.memberStatus) params.set('member_status', request.memberStatus);
	return params;
}

export function buildTeamMembersQueryParams(request: TeamMembersRequest): URLSearchParams {
	const params = new URLSearchParams();
	const companyId = present(request.companyId);
	if (companyId) params.set('company_id', companyId);
	const accountTeamId = present(request.accountTeamId);
	if (accountTeamId) params.set('team_id', accountTeamId);

	const cursor = present(request.cursor);
	if (cursor) params.set('cursor', cursor);
	if (request.limit !== undefined && request.limit !== null) {
		params.set('limit', String(clampTeamsLimit(request.limit)));
	}
	if (request.sort && request.sort !== TEAM_MEMBERS_DEFAULT_SORT) params.set('sort', request.sort);
	if (request.order === 'desc') params.set('order', 'desc');

	const q = present(request.q);
	if (q) params.set('q', q.slice(0, TEAMS_Q_MAX_LENGTH));
	if (request.status) params.set('status', request.status);
	const teamRoleId = present(request.teamRoleId);
	if (teamRoleId) params.set('team_role_id', teamRoleId);
	return params;
}

function stringOrNull(value: unknown): string | null {
	return typeof value === 'string' && value ? value : null;
}

function parseRoleRef(candidate: unknown): TeamRoleRef | null {
	const role = objectRecord(candidate);
	const id = role ? stringOrNull(role.id) : null;
	if (!role || !id) return null;
	return { id, name: typeof role.name === 'string' ? role.name : null };
}

function parseTeamListRow(candidate: unknown): TeamListRow | null {
	const row = objectRecord(candidate);
	const id = row ? stringOrNull(row.id) : null;
	if (!row || !id) return null;
	const membership = objectRecord(row.membership);
	return {
		id,
		name: typeof row.name === 'string' ? row.name : '',
		created: stringOrNull(row.created),
		isActive: row.is_active === undefined ? true : !!row.is_active,
		customFields: Array.isArray(row.custom_fields)
			? row.custom_fields
				.map(objectRecord)
				.filter((field): field is Record<string, unknown> => !!field && !!stringOrNull(field.key))
				.map((field) => ({
					key: field.key as string,
					label: typeof field.label === 'string' ? field.label : '',
					value: stringOrNull(field.value),
				}))
			: [],
		memberCount: count(row.member_count),
		pendingCount: count(row.pending_count),
		membership: membership
			? {
				joined: stringOrNull(membership.joined),
				invited: stringOrNull(membership.invited),
				isPending: !!membership.is_pending,
				teamRole: parseRoleRef(membership.team_role),
			}
			: null,
	};
}

function parseTeamMemberTeamRef(candidate: unknown): TeamMemberTeamRef | null {
	const team = objectRecord(candidate);
	const id = team ? stringOrNull(team.id) : null;
	if (!team || !id) return null;
	return {
		id,
		name: typeof team.name === 'string' ? team.name : '',
		pending: !!team.pending,
		isActive: team.is_active === undefined ? true : !!team.is_active,
	};
}

function parseTeamMemberListRow(candidate: unknown): TeamMemberListRow | null {
	const row = objectRecord(candidate);
	if (!row) return null;
	const email = stringOrNull(row.email) || '';
	const key = stringOrNull(row.key) || email;
	if (!key) return null;
	return {
		key,
		email,
		userId: stringOrNull(row.user_id),
		invited: stringOrNull(row.invited),
		joined: stringOrNull(row.joined),
		isPending: !!row.is_pending,
		companyRoleInviteOnly: !!row.company_role_invite_only,
		teamRole: parseRoleRef(row.team_role),
		companyRole: parseRoleRef(row.company_role),
		teams: Array.isArray(row.teams)
			? row.teams.map(parseTeamMemberTeamRef).filter((team): team is TeamMemberTeamRef => team !== null)
			: [],
	};
}

function parseListPage<R>(body: unknown, parseRow: (candidate: unknown) => R | null): ListPage<R> {
	const envelope = objectRecord(body) || {};
	return {
		rows: Array.isArray(envelope.data)
			? envelope.data.map(parseRow).filter((row: R | null): row is R => row !== null)
			: [],
		nextCursor: stringOrNull(envelope.next_cursor),
		hasMore: !!envelope.has_more,
	};
}

export function parseTeamsListPage(body: unknown): ListPage<TeamListRow> {
	return parseListPage(body, parseTeamListRow);
}

export function parseTeamMembersPage(body: unknown): ListPage<TeamMemberListRow> {
	return parseListPage(body, parseTeamMemberListRow);
}

/** One GET of a listing, as a result union. INVALID_CURSOR is its own outcome: the table starts over. */
async function fetchListing<R>(
	url: string,
	idToken: string,
	fetchImpl: typeof fetch,
	parse: (body: unknown) => ListPage<R>,
): Promise<ListFetchResult<R>> {
	let resp: Response;
	try {
		resp = await fetchImpl(url, { method: 'GET', headers: authedHeaders(idToken) });
	} catch {
		return { ok: false, reason: 'network' };
	}
	if (!resp.ok) {
		const { code, message } = await readErrorEnvelope(resp);
		const reason: ListFetchFailure['reason'] = code === 'INVALID_CURSOR'
			? 'stale-cursor'
			: (resp.status === 401 ? 'unauthorized' : (resp.status === 403 ? 'forbidden' : 'failed'));
		return {
			ok: false,
			reason,
			...(code ? { code } : {}),
			message: message || `HTTP ${resp.status}`,
		};
	}
	let body: unknown;
	try {
		body = await resp.json();
	} catch {
		return { ok: false, reason: 'failed', message: 'Invalid JSON' };
	}
	return { ok: true, page: parse(body) };
}

export interface ListFetchInput<Q> {
	baseUrl: string;
	idToken: string;
	request: Q;
	fetchImpl?: typeof fetch;
}

/** One page of the company's teams. */
export async function fetchTeamsPage(input: ListFetchInput<TeamsListRequest>): Promise<ListFetchResult<TeamListRow>> {
	if (!present(input.request.companyId)) {
		return { ok: false, reason: 'failed', code: 'COMPANY_REQUIRED', message: 'A company is required' };
	}
	const qs = buildTeamsListQueryParams(input.request).toString();
	const url = `${input.baseUrl.replace(/\/$/, '')}/teams?${qs}`;
	return fetchListing(url, input.idToken, input.fetchImpl || window.fetch, parseTeamsListPage);
}

/** One page of one team's members. */
export async function fetchTeamMembersPage(input: ListFetchInput<TeamMembersRequest>): Promise<ListFetchResult<TeamMemberListRow>> {
	const teamId = present(input.request.teamId);
	if (!teamId) {
		return { ok: false, reason: 'failed', code: 'COMPANY_REQUIRED', message: 'A team is required' };
	}
	const qs = buildTeamMembersQueryParams(input.request).toString();
	const url = `${input.baseUrl.replace(/\/$/, '')}/teams/${encodeURIComponent(teamId)}/members?${qs}`;
	return fetchListing(url, input.idToken, input.fetchImpl || window.fetch, parseTeamMembersPage);
}
